import { ProDocExtractionReview, extractionWalkOf, walkAt, type ExtractionStep } from '@fiveox/prodoc-ui'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router'

import { useOrg } from '../components/org-context'
import { SERVICE_API } from '../lib/config'
import { session } from '../lib/session'

/** An extraction's own address — with `?from=queue` when it is reviewed from the queue. */
export const extractionAddress = (step: ExtractionStep, fromQueue: boolean): string =>
  `/documents/${encodeURIComponent(step.document_id)}/extractions/${encodeURIComponent(step.id)}${fromQueue ? '?from=queue' : ''}`

/**
 * One extraction, at its own address (engine W15, FR-E20, plan D32) —
 * `/documents/:id/extractions/:eid`, so a link from the review queue, the
 * document's Extractions tab or a notification survives sign-in. A thin host
 * (ADR-0010): the screen is `<ProDocExtractionReview>`, which reads, corrects
 * and decides for a reviewer and only reads for anyone else; the route adds
 * the way back — to the queue when it came from there (`?from=queue`), else to
 * the document.
 *
 * W24 (FR-56, D73): **opened from the queue, it walks the queue's order**, which
 * the queue page left in the route's state (`walk`, ids only). Previous / Next
 * and J / K open a neighbour with the same walk; a decision opens the next, and
 * the last one decided goes back to the queue. Without a walk — a link, a
 * notification — it is reviewed on its own, as before.
 */
export function ExtractionPage() {
  const { id = '', eid = '' } = useParams()
  const [params] = useSearchParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { org, userId } = useOrg()
  const fromQueue = params.get('from') === 'queue'
  const walk = fromQueue ? extractionWalkOf((location.state as { walk?: unknown } | null)?.walk) : null
  const open = (step: ExtractionStep) => navigate(extractionAddress(step, true), { state: { walk } })
  const { next } = walkAt(walk, eid)

  return (
    <ProDocExtractionReview
      apiUrl={SERVICE_API}
      fetcher={session.authed}
      orgId={org.id}
      userId={userId}
      documentId={id}
      extractionId={eid}
      walk={walk}
      onWalk={walk ? open : undefined}
      back={
        <Link to={fromQueue ? '/extractions' : `/documents/${encodeURIComponent(id)}`} className="text-muted-foreground hover:text-foreground text-sm">
          {fromQueue ? '← Extractions' : '← The document'}
        </Link>
      }
      onOpenDocument={(doc) => navigate(`/documents/${encodeURIComponent(doc.id)}`)}
      // Decided from the queue: on to the next in its order, or back to what still waits after the last.
      onDecided={() => {
        if (next) open(next)
        else if (fromQueue) navigate('/extractions')
      }}
    />
  )
}
